import { get, orderBy, uniqBy, isEqual, cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';

import { consultationTypes, consultationTypesMessages } from 'modules/common/constants/consultationTypes';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { CONVERSATION_TYPES as conversationTypesMessages } from 'modules/appointmentsManagement/constants/conversationTypes';

const getSessionInfo = state => get(state, 'session.sessionInfo');
const getAppointmentServiceProviders = state => get(state, 'appointmentsManagement.appointmentConfig.providers', {});
export const getOrganizations = state => get(state, 'appointmentsManagement.organizationsTree.organizations');
const getContracts = state => get(state, 'appointmentsManagement.appointmentConfig.contractsList', []);
export const getTimeSlots = state => get(state, 'appointmentsManagement.appointmentConfig.timeSlots', []);
export const getCalendarTimeSlots = state => get(state, 'appointmentsManagement.appointmentConfig.calendarTimeSlots', []);

const mapOptions = contracts => {
  if (!contracts || !contracts.length) return [];

  const mappedContracts = contracts.map(contract => ({
    id: contract.contractId,
    providingUnitName: contract.service.name,
    customerUnitName: contract.customerOrgNodeName,
  }));

  const sortedContracts = orderBy(mappedContracts, [
    item => item.providingUnitName.toLowerCase(),
    item => item.customerUnitName.toLowerCase(),
  ], ['asc', 'asc']);

  return sortedContracts.map(contract => ({
    key: contract.id,
    value: `${ contract.providingUnitName } - ${ contract.customerUnitName }`,
  }));
};

export const getContractsOptions = createSelector(
  [getContracts],
  mapOptions,
);

export const getConversationTypesOptions = createSelector(
  [intl => intl, (_, selectedService = {}) => get(selectedService, 'service.conversationTypes')],
  (intl, conversationTypes) => conversationTypes && conversationTypes.filter(item => item !== 'TEXT').map(item => ({
    key: item,
    value: intl.formatMessage(conversationTypesMessages[item]),
  })),
);

export const getSelectedContract = createSelector(
  [getContracts, (_, contractId) => contractId],
  (contracts, contractId) => {
    return cloneDeep(contracts.find(item => item.contractId === contractId));
  },
);

const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  (currValue, prevValue) => currValue === prevValue || isEqual(currValue, prevValue),
);

export const getTimeSlotsOptions = createDeepEqualSelector(
  [getTimeSlots],
  timeSlots => {
    const slots = timeSlots.map((item) => ({
      key: `${ item.from }-${ item.to }`,
      value: `${ moment(item.from).format('HH:mm') } - ${ moment(item.to).format('HH:mm') }`,
    }));

    return uniqBy(slots, item => item.key);
  },
);

export const formatTimeSlots = timeSlots => {
  const slots = orderBy(timeSlots, 'from');

  const result = [];
  slots.forEach(item => {
    const slot = { ...item };
    if (!result.length) result.push(slot);
    const findIndex = result.findIndex(curr => curr.to === slot.from);
    if (findIndex > -1) {
      result[findIndex].to = slot.to;
    } if (!result.some(item => moment(slot.from).isBetween(item.from, item.to))) result.push(slot);
  });

  return uniqBy(result, item => item.from);
};

const slotInterval = 60000;

export const getFlexibleToSlotsOptions = createDeepEqualSelector(
  [getTimeSlots, (state, serviceConfig) => serviceConfig, (state, serviceConfig, formValues) => formValues],
  (timeSlotsRaw, selectedServiceConfig, formValues) => {
    const timeSlots = formatTimeSlots(timeSlotsRaw);

    const endTimeSlots = [];

    if (!timeSlots.length || !selectedServiceConfig) return endTimeSlots;

    const startTime = get(formValues, 'flexible.from');
    if (!startTime) return endTimeSlots;

    const selectedTimeSlot = timeSlots.find(item => startTime >= item.from && startTime < item.to);
    if (!selectedTimeSlot) return endTimeSlots;

    const { fixedAppointmentDuration, maxAppointmentDuration } = selectedServiceConfig;

    let endTime;
    if (fixedAppointmentDuration) {
      endTime = moment(startTime).add(fixedAppointmentDuration, 'minute');

      endTimeSlots.push({
        value: endTime.format('HH:mm'),
        key: endTime.valueOf(),
      });
    } else if (maxAppointmentDuration) {
      endTime = moment(startTime).add(maxAppointmentDuration, 'minute').valueOf();
      if (endTime > selectedTimeSlot.to) endTime = selectedTimeSlot.to;
    } else {
      endTime = moment(selectedTimeSlot.to);
    }

    const endDate = moment(formValues.endDate).startOf('day').valueOf();
    const isSameEndDate = moment(formValues.endDate).isSame(startTime, 'day');
    const maxTime = isSameEndDate
      ? moment(formValues.endDate).endOf('day').valueOf()
      : moment(startTime).add(1, 'day').valueOf();
    const endTimeValue = endTime.valueOf();
    let nextTimeInterval = startTime;

    while (nextTimeInterval < endTimeValue && nextTimeInterval < maxTime) {
      nextTimeInterval += slotInterval;
      if (nextTimeInterval > endTimeValue) nextTimeInterval = endTimeValue;

      if (nextTimeInterval >= endDate && nextTimeInterval <= maxTime) {
        endTimeSlots.push({
          key: moment(nextTimeInterval).valueOf(),
          value: moment(nextTimeInterval).format('HH:mm'),
        });
      }
    }

    return uniqBy(endTimeSlots, item => item.key);
  },
);

export const getFlexibleFromSlotsOptions = createDeepEqualSelector(
  [getTimeSlots, (state, selectedServiceConfig) => selectedServiceConfig, (state, config, startDate) => startDate],
  (timeSlotsRaw, selectedServiceConfig, startDate) => {
    const timeSlots = formatTimeSlots(timeSlotsRaw);
    const startTimeSlots = [];
    const { fixedAppointmentDuration } = selectedServiceConfig;

    timeSlots.forEach(item => {
      const endTimeValue = !fixedAppointmentDuration
        ? moment(item.to).startOf('minute').valueOf()
        : moment(item.to).subtract(fixedAppointmentDuration - 1, 'minute').startOf('minute').valueOf();

      let nextTimeInterval = item.from;
      if (moment(nextTimeInterval).startOf('minute').valueOf() !== nextTimeInterval) {
        nextTimeInterval = moment(nextTimeInterval).add(1, 'minute').startOf('minute').valueOf();
      }

      const startDateValue = moment(startDate).endOf('day').valueOf();

      while (nextTimeInterval < endTimeValue && nextTimeInterval <= startDateValue) {
        startTimeSlots.push({
          key: nextTimeInterval,
          value: moment(nextTimeInterval).format('HH:mm'),
        });

        nextTimeInterval += slotInterval;
      }
    });

    return uniqBy(startTimeSlots, item => item.key);
  },
);

export const getApptTimeSlots = createSelector(
  [getTimeSlots],
  operationTimeSlots => cloneDeep(operationTimeSlots),
);

export const sessionInitialized = createSelector(
  [getSessionInfo],
  (sessionInfo) => !isEmpty(sessionInfo) && !!sessionInfo.sessionId && sessionInfo.requestStatus === requestsStatuses.success,
);

export const getAppointmentProvidersOptions = createSelector(
  [getAppointmentServiceProviders, (_, intl) => intl, (_, intl, isProviderRequired) => isProviderRequired],
  (providers, intl, isProviderRequired) => {
    if (isEmpty(providers)) return [];

    const providersGroup = { key: 'PROVIDERS_GROUP', value: intl.formatMessage({ id: 'General.ProvidersGroup', defaultMessage: '** Providers group' }) };
    const result = providers.providers.map(item => ({
      key: item.userId,
      value: `${ item.fullName } ${ !item.isAvailable ? intl.formatMessage({ id: 'Clinician.Appointment.Handoff.Create.Provider.Unavailable', defaultMessage: '** (Unavailable)' }) : '' }`, // eslint-disable-line
      disabled: !item.isAvailable,
    }));
    if (!isProviderRequired && providers.isProviderGroupAvailable) result.unshift(providersGroup);

    return result;
  },
);

export const getApptCalendarTimeSlots = createSelector(
  [getCalendarTimeSlots],
  operationTimeSlots => cloneDeep(operationTimeSlots),
);

export const getConsultationTypesOptions = createSelector(
  [selectedContract => selectedContract],
  (selectedContract) =>
    consultationTypes.map(item => ({
      key: item,
      label: consultationTypesMessages[item],
      icon: consultationTypesMessages[item].icon,
      disabled: !get(selectedContract, 'service.consultationTypes', []).includes(item),
    })),
);
